import React, { useEffect, useState, useCallback } from 'react';
import Nlayout from './Nlayout';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoRefreshOutline,
  IoSearchOutline,
  IoCopyOutline,
  IoCheckmarkOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';
import { FaTrophy, FaShoppingCart, FaUserCheck, FaCoins } from 'react-icons/fa';
import { MdOutlineCasino, MdOutlineHistoryEdu } from 'react-icons/md';
import './AcreditacionesTipeo.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend.eldenguee.com';
const API_KEY = process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026';
const API_HEADERS = {
  'x-internal-api-key': API_KEY,
  'x-api-key': API_KEY,
};

function formatFecha(fechaStr) {
  if (!fechaStr) return '—';
  try {
    const d = new Date(fechaStr);
    if (isNaN(d.getTime())) return String(fechaStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return String(fechaStr);
  }
}

export default function AcreditacionesTipeo() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    acreditados: 0,
    pendientes: 0,
    porSorteo: 0,
    porCompra: 0,
    porManual: 0,
    totalTipeosOtorgados: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
    hasMore: false,
  });

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [origenFiltro, setOrigenFiltro] = useState('todos');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');

  // Estados de carga y feedback
  const [loading, setLoading] = useState(false);
  const [reintentandoId, setReintentandoId] = useState(null);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [copiadoId, setCopiadoId] = useState(null);

  // Modal para acreditación manual
  const [modalManualOpen, setModalManualOpen] = useState(false);
  const [formManual, setFormManual] = useState({
    username: '',
    cantidad: 1,
    casino: '1xBet',
    origen: 'manual',
    sorteoId: '',
    sorteoTitulo: '',
    detalles: '',
  });
  const [guardandoManual, setGuardandoManual] = useState(false);

  const cargarDatos = useCallback(async (page = 1, busq = busqueda, orig = origenFiltro, est = estadoFiltro) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '25',
      });
      if (busq && busq.trim()) params.append('busqueda', busq.trim());
      if (orig && orig !== 'todos') params.append('origen', orig);
      if (est && est !== 'todos') params.append('estado', est);

      const endpoints = [
        `${API_BASE}/sorteo/acreditaciones?${params.toString()}`,
        `${API_BASE}/solicitudes/acreditaciones?${params.toString()}`,
      ];

      let res = null;
      let lastError = null;
      for (const ep of endpoints) {
        try {
          res = await axios.get(ep, { headers: API_HEADERS });
          if (res?.data?.success) break;
        } catch (endpointErr) {
          lastError = endpointErr;
        }
      }

      if (res?.data?.success) {
        setItems(res.data.items || []);
        if (res.data.stats) setStats(res.data.stats);
        if (res.data.pagination) setPagination(res.data.pagination);
        if (res.data.tablaPendiente) {
          setMensaje({
            tipo: 'error',
            texto: '⚠️ La tabla dbo.historial_acreditaciones_tipeo aún no existe en SQL Server. Ejecutá el script de migración SQL en la base de datos.',
          });
        }
      } else {
        const errorMsg =
          lastError?.response?.data?.detalle ||
          lastError?.response?.data?.error ||
          lastError?.message ||
          'No se pudo cargar el historial de acreditaciones.';
        setMensaje({ tipo: 'error', texto: `Error en el servidor: ${errorMsg}` });
      }
    } catch (err) {
      console.error('Error al cargar acreditaciones:', err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el historial de acreditaciones.' });
    } finally {
      setLoading(false);
    }
  }, [busqueda, origenFiltro, estadoFiltro]);

  useEffect(() => {
    cargarDatos(1);
  }, [cargarDatos]);

  const handleBuscar = (e) => {
    e.preventDefault();
    cargarDatos(1, busqueda, origenFiltro, estadoFiltro);
  };

  const handleLimpiarFiltros = () => {
    setBusqueda('');
    setOrigenFiltro('todos');
    setEstadoFiltro('todos');
    cargarDatos(1, '', 'todos', 'todos');
  };

  const handleReintentar = async (item) => {
    if (!item?.id) return;
    setReintentandoId(item.id);
    try {
      const endpoints = [
        `${API_BASE}/sorteo/acreditaciones/${item.id}/reintentar`,
        `${API_BASE}/solicitudes/acreditaciones/${item.id}/reintentar`,
      ];
      let res = null;
      for (const ep of endpoints) {
        try {
          res = await axios.post(ep, { admin: 'admin' }, { headers: API_HEADERS });
          if (res?.data?.success) break;
        } catch (_) {}
      }

      if (res?.data?.success) {
        setMensaje({ tipo: 'success', texto: res.data.message || `Tipeo acreditado a ${item.username}.` });
        cargarDatos(pagination.page);
      } else {
        throw new Error(res?.data?.error || 'No se pudo reintentar la acreditación');
      }
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || err.message || 'Error al acreditar.' });
    } finally {
      setReintentandoId(null);
    }
  };

  const handleAcreditarManual = async () => {
    if (!formManual.username || !formManual.username.trim()) {
      alert('Por favor ingresá el nombre de usuario.');
      return;
    }
    setGuardandoManual(true);
    try {
      const res = await axios.post(
        `${API_BASE}/sorteo/acreditaciones/manual`,
        {
          username: formManual.username.trim(),
          cantidad: parseInt(formManual.cantidad, 10) || 1,
          casino: formManual.casino,
          origen: formManual.origen,
          sorteoId: formManual.sorteoId || null,
          sorteoTitulo: formManual.sorteoTitulo || null,
          detalles: formManual.detalles || 'Acreditación manual desde el panel',
          admin: 'admin',
        },
        { headers: API_HEADERS }
      );

      if (res?.data?.success) {
        setMensaje({ tipo: 'success', texto: res.data.message });
        setModalManualOpen(false);
        setFormManual({
          username: '',
          cantidad: 1,
          casino: '1xBet',
          origen: 'manual',
          sorteoId: '',
          sorteoTitulo: '',
          detalles: '',
        });
        cargarDatos(1);
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error al guardar acreditación manual');
    } finally {
      setGuardandoManual(false);
    }
  };

  const copiarSorteoId = (id) => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiadoId(id);
    setTimeout(() => setCopiadoId(null), 2000);
  };

  return (
    <Nlayout>
      <div className="acred-container">
        {/* Encabezado */}
        <div className="acred-header-box">
          <div>
            <h1 className="acred-title">Control de Acreditación de Tipeos</h1>
            <p className="acred-subtitle">
              Historial y trazabilidad de tipeos otorgados a jugadores por sorteos ganados, compras en tienda y asignaciones manuales.
            </p>
          </div>
          <div className="acred-header-actions">
            <Button
              variant="outlined"
              startIcon={<IoRefreshOutline />}
              onClick={() => cargarDatos(pagination.page)}
              disabled={loading}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '12px',
                px: 2,
                '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168,85,247,0.1)' },
              }}
            >
              {loading ? 'Actualizando...' : 'Actualizar'}
            </Button>
            <Button
              variant="contained"
              startIcon={<FaCoins />}
              onClick={() => setModalManualOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #9333ea, #7c3aed)',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '12px',
                px: 2.5,
                boxShadow: '0 4px 15px rgba(147, 51, 234, 0.4)',
                '&:hover': { background: 'linear-gradient(135deg, #a855f7, #6d28d9)' },
              }}
            >
              Nueva Acreditación
            </Button>
          </div>
        </div>

        {/* Tarjetas de Métricas de Resumen */}
        <div className="acred-stats-grid">
          <div className="acred-stat-card">
            <div className="acred-stat-icon-wrapper acred-stat-icon-green">
              <IoCheckmarkCircle />
            </div>
            <div>
              <div className="acred-stat-label">Acreditados</div>
              <div className="acred-stat-value">{stats.acreditados}</div>
            </div>
          </div>

          <div
            className="acred-stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setEstadoFiltro('pendientes');
              cargarDatos(1, busqueda, origenFiltro, 'pendientes');
            }}
          >
            <div className="acred-stat-icon-wrapper acred-stat-icon-red">
              <IoAlertCircleOutline />
            </div>
            <div>
              <div className="acred-stat-label">No Acreditados / Fallos</div>
              <div className="acred-stat-value" style={{ color: stats.pendientes > 0 ? '#ef4444' : '#fff' }}>
                {stats.pendientes}
              </div>
            </div>
          </div>

          <div className="acred-stat-card">
            <div className="acred-stat-icon-wrapper acred-stat-icon-purple">
              <FaTrophy />
            </div>
            <div>
              <div className="acred-stat-label">Por Sorteo</div>
              <div className="acred-stat-value">{stats.porSorteo}</div>
            </div>
          </div>

          <div className="acred-stat-card">
            <div className="acred-stat-icon-wrapper acred-stat-icon-blue">
              <FaShoppingCart />
            </div>
            <div>
              <div className="acred-stat-label">Por Compra (Tienda)</div>
              <div className="acred-stat-value">{stats.porCompra}</div>
            </div>
          </div>
        </div>

        {/* Barra de Filtros y Búsqueda */}
        <form onSubmit={handleBuscar} className="acred-toolbar">
          <div className="acred-search-input">
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por jugador, ID de sorteo, título o motivo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              InputProps={{
                startAdornment: <IoSearchOutline style={{ color: '#94a3b8', marginRight: 8, fontSize: 18 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: '12px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(168,85,247,0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                },
              }}
            />
          </div>

          <div className="acred-filters-row">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Origen</InputLabel>
              <Select
                value={origenFiltro}
                label="Origen"
                onChange={(e) => {
                  setOrigenFiltro(e.target.value);
                  cargarDatos(1, busqueda, e.target.value, estadoFiltro);
                }}
                sx={{
                  color: '#fff',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
                }}
              >
                <MenuItem value="todos">Todos los orígenes</MenuItem>
                <MenuItem value="sorteo">🎰 Sorteo</MenuItem>
                <MenuItem value="compra">🛒 Compra (Tienda)</MenuItem>
                <MenuItem value="manual">⚙️ Manual (Admin)</MenuItem>
                <MenuItem value="codigo">🎟️ Código canjeado</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Estado</InputLabel>
              <Select
                value={estadoFiltro}
                label="Estado"
                onChange={(e) => {
                  setEstadoFiltro(e.target.value);
                  cargarDatos(1, busqueda, origenFiltro, e.target.value);
                }}
                sx={{
                  color: '#fff',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
                }}
              >
                <MenuItem value="todos">Todos los estados</MenuItem>
                <MenuItem value="acreditados">✅ Acreditados</MenuItem>
                <MenuItem value="pendientes">❌ No Acreditados</MenuItem>
              </Select>
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              sx={{
                background: '#a855f7',
                color: '#fff',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.2,
                '&:hover': { background: '#9333ea' },
              }}
            >
              Filtrar
            </Button>

            {(busqueda || origenFiltro !== 'todos' || estadoFiltro !== 'todos') && (
              <Button
                variant="text"
                onClick={handleLimpiarFiltros}
                sx={{ color: '#94a3b8', textTransform: 'none', '&:hover': { color: '#fff' } }}
              >
                Limpiar
              </Button>
            )}
          </div>
        </form>

        {/* Mensaje de feedback */}
        {mensaje.texto && (
          <Box
            mb={2}
            p={1.5}
            sx={{
              borderRadius: '12px',
              backgroundColor: mensaje.tipo === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
              border: `1px solid ${mensaje.tipo === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(16,185,129,0.4)'}`,
              color: mensaje.tipo === 'error' ? '#f87171' : '#34d399',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2">{mensaje.texto}</Typography>
            <IconButton size="small" onClick={() => setMensaje({ tipo: '', texto: '' })} sx={{ color: 'inherit' }}>
              <IoCloseCircle />
            </IconButton>
          </Box>
        )}

        {/* Tabla Principal */}
        <div className="acred-table-container">
          <TableContainer>
            <Table className="acred-table">
              <TableHead>
                <TableRow>
                  <TableCell className="acred-th">Jugador</TableCell>
                  <TableCell className="acred-th">¿Se Acreditó?</TableCell>
                  <TableCell className="acred-th">Fecha y Hora</TableCell>
                  <TableCell className="acred-th" align="center">Tenía Antes</TableCell>
                  <TableCell className="acred-th" align="center">Acreditado</TableCell>
                  <TableCell className="acred-th" align="center">Tiene Después</TableCell>
                  <TableCell className="acred-th">De Qué Manera</TableCell>
                  <TableCell className="acred-th">ID Sorteo</TableCell>
                  <TableCell className="acred-th">Casino / Motivo</TableCell>
                  <TableCell className="acred-th" align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={36} sx={{ color: '#a855f7' }} />
                      <Typography sx={{ mt: 2, color: '#94a3b8' }}>Cargando acreditaciones...</Typography>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <div className="acred-empty">
                        <MdOutlineHistoryEdu style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }} />
                        <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 700 }}>
                          No se encontraron registros de acreditación
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                          Cuando los jugadores ganen un sorteo, compren tipeos o se les asigne saldo, quedará registrado automáticamente aquí.
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => {
                    const isSorteo = String(row.origen).toLowerCase() === 'sorteo';
                    const isCompra = String(row.origen).toLowerCase() === 'compra';
                    const isManual = String(row.origen).toLowerCase() === 'manual';
                    const isCodigo = String(row.origen).toLowerCase() === 'codigo';
                    const initialLetter = (row.username || 'U').charAt(0).toUpperCase();

                    return (
                      <TableRow key={row.id} className="acred-tr">
                        {/* 1. Jugador */}
                        <TableCell className="acred-td">
                          <div className="acred-user-chip">
                            <div className="acred-avatar">{initialLetter}</div>
                            <div>
                              <div className="acred-username">{row.username}</div>
                              {row.acreditado_por && (
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
                                  Por: {row.acreditado_por}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        {/* 2. ¿Se Acreditó? */}
                        <TableCell className="acred-td">
                          {row.acreditado ? (
                            <Tooltip title={`Acreditado el ${formatFecha(row.fecha_acreditacion || row.created_at)}`}>
                              <span className="badge-acreditado">
                                <IoCheckmarkCircle size={14} /> Acreditado
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="No se acreditó o hubo un error">
                              <span className="badge-pendiente">
                                <IoCloseCircle size={14} /> No acreditado
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* 3. Fecha y Hora */}
                        <TableCell className="acred-td">
                          <Typography variant="body2" sx={{ color: '#e2e8f0', fontSize: 13, fontWeight: 500 }}>
                            {formatFecha(row.fecha_acreditacion || row.created_at)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
                            Registro: {formatFecha(row.created_at)}
                          </Typography>
                        </TableCell>

                        {/* 4. Cuánto tenía antes */}
                        <TableCell className="acred-td" align="center">
                          <span className="chip-saldo-anterior">
                            {row.saldo_anterior} tipeo{row.saldo_anterior === 1 ? '' : 's'}
                          </span>
                        </TableCell>

                        {/* 5. Acreditado (+N) */}
                        <TableCell className="acred-td" align="center">
                          <span className="chip-cantidad">+{row.cantidad || 1}</span>
                        </TableCell>

                        {/* 6. Saldo posterior */}
                        <TableCell className="acred-td" align="center">
                          <span className="chip-saldo-posterior">
                            {row.saldo_posterior} tipeo{row.saldo_posterior === 1 ? '' : 's'}
                          </span>
                        </TableCell>

                        {/* 7. De qué manera */}
                        <TableCell className="acred-td">
                          {isSorteo && <span className="badge-origen-sorteo">🎰 Sorteo</span>}
                          {isCompra && <span className="badge-origen-compra">🛒 Compra Tienda</span>}
                          {isManual && <span className="badge-origen-manual">⚙️ Manual Admin</span>}
                          {isCodigo && <span className="badge-origen-codigo">🎟️ Código</span>}
                          {!isSorteo && !isCompra && !isManual && !isCodigo && (
                            <span className="badge-origen-manual">{row.origen}</span>
                          )}
                        </TableCell>

                        {/* 8. Si es sorteo, indica ID del sorteo */}
                        <TableCell className="acred-td">
                          {isSorteo && row.sorteo_id ? (
                            <div>
                              <Tooltip title="Copiar ID del sorteo">
                                <span
                                  className="acred-sorteo-pill"
                                  onClick={() => copiarSorteoId(row.sorteo_id)}
                                >
                                  {copiadoId === row.sorteo_id ? (
                                    <IoCheckmarkOutline style={{ color: '#34d399' }} />
                                  ) : (
                                    <IoCopyOutline />
                                  )}
                                  <span>{row.sorteo_id.length > 12 ? `${row.sorteo_id.substring(0, 10)}...` : row.sorteo_id}</span>
                                </span>
                              </Tooltip>
                              {row.sorteo_titulo && (
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5, fontSize: 11 }} numberOfLines={1}>
                                  {row.sorteo_titulo}
                                </Typography>
                              )}
                            </div>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* 9. Casino / Motivo */}
                        <TableCell className="acred-td">
                          {row.casino && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                              <MdOutlineCasino style={{ color: '#a855f7', fontSize: 14 }} />
                              <Typography variant="caption" sx={{ color: '#c084fc', fontWeight: 700 }}>
                                {row.casino}
                              </Typography>
                            </div>
                          )}
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11.5, display: 'block' }}>
                            {row.detalles || 'Acreditación registrada'}
                          </Typography>
                        </TableCell>

                        {/* 10. Acciones */}
                        <TableCell className="acred-td" align="right">
                          {!row.acreditado && (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={reintentandoId === row.id}
                              onClick={() => handleReintentar(row)}
                              sx={{
                                background: '#10b981',
                                color: '#fff',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: 11,
                                borderRadius: '8px',
                                py: 0.4,
                                px: 1.2,
                                '&:hover': { background: '#059669' },
                              }}
                            >
                              {reintentandoId === row.id ? 'Acreditando...' : 'Acreditar ahora'}
                            </Button>
                          )}
                          {Boolean(row.acreditado) && (
                            <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: 11 }}>
                              Completado
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          {pagination.total > 0 && (
            <div className="acred-pagination">
              <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: 13 }}>
                Mostrando página {pagination.page} de {pagination.totalPages} ({pagination.total} registros en total)
              </Typography>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => cargarDatos(pagination.page - 1)}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '8px',
                  }}
                >
                  Anterior
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={pagination.page >= pagination.totalPages || loading}
                  onClick={() => cargarDatos(pagination.page + 1)}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '8px',
                  }}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Modal: Acreditación Manual */}
        <Modal open={modalManualOpen} onClose={() => setModalManualOpen(false)}>
          <div className="acred-modal-content">
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, color: '#fff' }}>
              Registrar Acreditación Manual
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2.5 }}>
              Otorga tipeos a un jugador y genera la trazabilidad en la nueva tabla de historial.
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                fullWidth
                size="small"
                label="Usuario / Jugador"
                value={formManual.username}
                onChange={(e) => setFormManual({ ...formManual, username: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { color: '#94a3b8' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&:hover fieldset': { borderColor: '#a855f7' },
                  },
                }}
              />

              <Box display="flex" gap={2}>
                <TextField
                  type="number"
                  size="small"
                  label="Cantidad de Tipeos"
                  value={formManual.cantidad}
                  onChange={(e) => setFormManual({ ...formManual, cantidad: e.target.value })}
                  sx={{
                    flex: 1,
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                    },
                  }}
                />

                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ color: '#94a3b8' }}>Casino</InputLabel>
                  <Select
                    value={formManual.casino}
                    label="Casino"
                    onChange={(e) => setFormManual({ ...formManual, casino: e.target.value })}
                    sx={{
                      color: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                    }}
                  >
                    <MenuItem value="1xBet">1xBet</MenuItem>
                    <MenuItem value="BetWinner">BetWinner</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: '#94a3b8' }}>Origen de la Acreditación</InputLabel>
                <Select
                  value={formManual.origen}
                  label="Origen de la Acreditación"
                  onChange={(e) => setFormManual({ ...formManual, origen: e.target.value })}
                  sx={{
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                  }}
                >
                  <MenuItem value="manual">⚙️ Asignación Manual</MenuItem>
                  <MenuItem value="sorteo">🎰 Ganador de Sorteo</MenuItem>
                  <MenuItem value="compra">🛒 Compra de Tipeo</MenuItem>
                  <MenuItem value="codigo">🎟️ Código Promocional</MenuItem>
                </Select>
              </FormControl>

              {formManual.origen === 'sorteo' && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label="ID del Sorteo"
                    value={formManual.sorteoId}
                    onChange={(e) => setFormManual({ ...formManual, sorteoId: e.target.value })}
                    placeholder="ej: e3b0c442-98fc-1c14-9afb-4c7fae98..."
                    sx={{
                      '& .MuiInputLabel-root': { color: '#94a3b8' },
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Título del Sorteo"
                    value={formManual.sorteoTitulo}
                    onChange={(e) => setFormManual({ ...formManual, sorteoTitulo: e.target.value })}
                    placeholder="ej: Sorteo Stream 1xBet"
                    sx={{
                      '& .MuiInputLabel-root': { color: '#94a3b8' },
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                      },
                    }}
                  />
                </>
              )}

              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Motivo / Notas"
                value={formManual.detalles}
                onChange={(e) => setFormManual({ ...formManual, detalles: e.target.value })}
                placeholder="Detalle o justificación para auditoría..."
                sx={{
                  '& .MuiInputLabel-root': { color: '#94a3b8' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  },
                }}
              />

              <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
                <Button
                  variant="text"
                  onClick={() => setModalManualOpen(false)}
                  sx={{ color: '#94a3b8', textTransform: 'none' }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  disabled={guardandoManual}
                  onClick={handleAcreditarManual}
                  sx={{
                    background: 'linear-gradient(135deg, #9333ea, #7c3aed)',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                  }}
                >
                  {guardandoManual ? 'Guardando...' : 'Acreditar y Registrar'}
                </Button>
              </Box>
            </Box>
          </div>
        </Modal>
      </div>
    </Nlayout>
  );
}

